/**
 * GoldFoilButton — primary editorial CTA.
 * Champagne-gold foil fill with charcoal text. Subtle metallic gradient.
 *
 * @example
 *   <GoldFoilButton href="/onboard">Open your studio</GoldFoilButton>
 *   <GoldFoilButton onClick={save} arrow={false} size="sm">Save draft</GoldFoilButton>
 *   <GoldFoilButton href="/admin" tone="dark">Enter studio</GoldFoilButton>
 */
import React from "react";

const SIZE = {
  sm: "px-5 py-2.5 text-[11px]",
  md: "px-7 py-3.5 text-[12px]",
  lg: "px-9 py-4 text-[13px]",
};

function Arrow({ className = "" }) {
  return (
    <svg className={className} width="14" height="10" viewBox="0 0 14 10" fill="none" aria-hidden="true">
      <path d="M0 5h12M8 1l4 4-4 4" stroke="currentColor" strokeWidth="1.25" strokeLinecap="square" />
    </svg>
  );
}

export default function GoldFoilButton({
  href,
  onClick,
  type = "button",
  size = "md",
  arrow = true,
  tone = "light", // "light" = on cream surface, "dark" = on charcoal surface
  className = "",
  children,
  ...rest
}) {
  const inner = (
    <span className="inline-flex items-center gap-3">
      <span className="uppercase tracking-[0.22em] font-medium">{children}</span>
      {arrow ? <Arrow className="transition-transform duration-300 group-hover:translate-x-1" /> : null}
    </span>
  );

  const base =
    "group inline-flex items-center justify-center font-sans rounded-none ed-focus";
  const fill =
    "text-[color:var(--charcoal)] bg-[linear-gradient(135deg,var(--gold-foil)_0%,var(--gold-base)_55%,var(--gold-deep)_100%)] shadow-[0_1px_0_rgba(255,255,255,0.4)_inset,0_8px_24px_-12px_rgba(168,137,64,0.6)] hover:brightness-105 active:brightness-95 transition";
  const focusClass = tone === "dark" ? "ed-focus-dark" : "ed-focus";
  const cls = `${base} ${focusClass.replace("ed-focus", "")} ${fill} ${SIZE[size] || SIZE.md} ${className}`;

  if (href) {
    return (
      <a href={href} className={cls} {...rest}>
        {inner}
      </a>
    );
  }
  return (
    <button type={type} onClick={onClick} className={cls} {...rest}>
      {inner}
    </button>
  );
}
